import { CollapsibleFilters } from '../../common/CollapsibleFilters';
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
  Alert,
} from "@mui/joy";
import {
  Plus as Add,
  PencilSimple as Edit,
  Trash as Delete,
  Play as PlayArrow,
  Pause,
  UsersThree as GroupAdd,
  Users as People,
} from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../../lib/api";
import type {
  DripCampaign,
  DripStep,
  DripTriggerType,
  EmailTemplate,
} from "../../../types/marketing";
import {
  ResponsiveTable,
  type ResponsiveColumn,
} from "../../common/ResponsiveTable";
import { DripEnrollmentDetails } from "./DripEnrollmentDetails";
import { EmailPreview } from "./EmailPreview";
import { emailError } from "./emailWorkflows";

const CONTACT_TYPES = [
  "lead",
  "prospect",
  "customer",
  "past_customer",
  "vendor",
];
const TRIGGERS: { value: DripTriggerType; label: string }[] = [
  { value: "manual", label: "Manual enrollment only" },
  { value: "tag_added", label: "When tag added" },
  { value: "contact_created", label: "When contact created" },
  { value: "lifecycle_changed", label: "When lifecycle stage changes" },
];

const empty: Partial<DripCampaign> = {
  name: "",
  trigger_type: "manual",
  trigger_tag: null,
  trigger_lifecycle_stage: "",
  steps: [],
};

export const DripCampaignsTab: React.FC = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [detailEnrollment, setDetailEnrollment] = useState<number | null>(null);
  const [sequence, setSequence] = useState<DripCampaign | null>(null);
  const [editing, setEditing] = useState<Partial<DripCampaign> | null>(null);
  const [enrollFor, setEnrollFor] = useState<DripCampaign | null>(null);
  const [enrollType, setEnrollType] = useState("");
  const [viewEnroll, setViewEnroll] = useState<number | null>(null);

  const {
    data: drips = [],
    isLoading,
    isError: dripsError,
    refetch: retryDrips,
  } = useQuery({
    queryKey: ["drip-campaigns"],
    queryFn: async () => (await api.getDripCampaigns()).data,
  });
  const { data: templates = [] } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => (await api.getEmailTemplates()).data,
  });
  const { data: tags = [] } = useQuery({
    queryKey: ["tags"],
    queryFn: async () =>
      (
        await api.get<{ tags: { id: number; name: string }[] }>(
          "/api/marketing/email-campaigns/audience-options/",
        )
      ).data.tags,
  });
  const {
    data: enrollments = [],
    isError: enrollmentsError,
    isFetching: enrollmentsLoading,
    refetch: retryEnrollments,
  } = useQuery({
    queryKey: ["drip-enrollments", viewEnroll],
    queryFn: async () => (await api.getDripEnrollments(viewEnroll!)).data,
    enabled: viewEnroll != null,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["drip-campaigns"] });

  const save = useMutation({
    mutationFn: async (d: Partial<DripCampaign>) =>
      d.id ? api.updateDripCampaign(d.id, d) : api.createDripCampaign(d),
    onSuccess: () => {
      invalidate();
      toast.success("Drip saved");
      setEditing(null);
    },
    onError: (error: unknown) => toast.error(emailError(error)),
  });
  const remove = useMutation({
    mutationFn: async (id: number) => api.deleteDripCampaign(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError: (error: unknown) => toast.error(emailError(error)),
  });
  const toggle = useMutation({
    mutationFn: async (d: DripCampaign) =>
      d.is_active
        ? api.deactivateDripCampaign(d.id)
        : api.activateDripCampaign(d.id),
    onSuccess: () => {
      invalidate();
      toast.success("Updated");
    },
    onError: (e: any) => toast.error(emailError(e)),
  });
  const enroll = useMutation({
    mutationFn: async () =>
      api.enrollDripContacts(enrollFor!.id, { contact_type: enrollType }),
    onSuccess: (r) => {
      toast.success(r.data.detail);
      setEnrollFor(null);
      setEnrollType("");
    },
    onError: () => toast.error("Failed to enroll"),
  });

  const setStep = (i: number, patch: Partial<DripStep>) => {
    const steps = [...(editing!.steps || [])];
    steps[i] = { ...steps[i], ...patch };
    setEditing({ ...editing!, steps });
  };
  const addStep = () => {
    const steps = [...(editing!.steps || [])];
    steps.push({
      order: steps.length,
      template: templates[0]?.id ?? 0,
      subject: "",
      delay_days: steps.length === 0 ? 0 : 2,
      delay_hours: 0,
    });
    setEditing({ ...editing!, steps });
  };
  const removeStep = (i: number) => {
    const steps = (editing!.steps || [])
      .filter((_, idx) => idx !== i)
      .map((s, idx) => ({ ...s, order: idx }));
    setEditing({ ...editing!, steps });
  };

  const columns: ResponsiveColumn<DripCampaign>[] = [
    {
      key: "name",
      header: "Name",
      mobileVariant: "header",
      width: "24%",
      sortValue: (row) => row.name,
      render: (d) => (
        <Typography level="title-sm" noWrap>
          {d.name}
        </Typography>
      ),
    },
    {
      key: "trigger",
      header: "Trigger",
      width: "20%",
      sortValue: (row) => row.trigger_type,
      render: (d) => <Typography level="body-sm">{d.trigger_type}</Typography>,
    },
    {
      key: "steps",
      header: "Steps",
      width: "10%",
      sortValue: (row) => row.steps?.length ?? 0,
      render: (d) => (
        <Typography level="body-sm">{d.steps?.length ?? 0}</Typography>
      ),
    },
    {
      key: "enrolled",
      header: "Active",
      width: "10%",
      sortValue: (row) => row.active_enrollments,
      render: (d) => (
        <Typography level="body-sm">{d.active_enrollments}</Typography>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "12%",
      sortValue: (row) => row.is_active,
      render: (d) => (
        <Chip
          size="sm"
          variant="soft"
          color={d.is_active ? "success" : "neutral"}
        >
          {d.is_active ? "Active" : "Inactive"}
        </Chip>
      ),
    },
  ];

  return (
    <Box>
      {dripsError && (
        <Alert
          color="danger"
          endDecorator={<Button onClick={() => retryDrips()}>Retry</Button>}
        >
          Sequences could not be loaded.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <CollapsibleFilters><Input
          aria-label="Search sequences"
          placeholder="Search sequences"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        /></CollapsibleFilters>
        <Button
          startDecorator={<Add />}
          onClick={() => setEditing({ ...empty, steps: [] })}
        >
          New Drip Campaign
        </Button>
      </Box>

      <ResponsiveTable<DripCampaign>
        rows={drips.filter((drip) =>
          drip.name.toLowerCase().includes(search.toLowerCase()),
        )}
        pagination={{ pageSize: 20, itemNoun: "sequences" }}
        getRowKey={(d) => d.id}
        columns={columns}
        actionsWidth="250px"
        rowActions={(d) => (
          <Stack
            direction="row"
            spacing={0.25}
            sx={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 0.25 }}
          >
            <IconButton
              size="sm"
              variant="plain"
              title="Enroll contacts"
              onClick={() => setEnrollFor(d)}
            >
              <GroupAdd size={18} />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              title="View enrollments"
              onClick={() => setViewEnroll(d.id)}
            >
              <People size={18} />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              title={d.is_active ? "Deactivate" : "Activate"}
              onClick={() => toggle.mutate(d)}
            >
              {d.is_active ? <Pause size={18} /> : <PlayArrow size={18} />}
            </IconButton>
            <Button size="sm" variant="plain" onClick={() => setSequence(d)}>
              Timeline
            </Button>
            <Button
              size="sm"
              variant="plain"
              onClick={async () => {
                try {
                  await api.post(
                    `/api/marketing/drip-campaigns/${d.id}/duplicate/`,
                    {},
                  );
                  await invalidate();
                  toast.success("A new inactive sequence copy is ready.");
                } catch (error) {
                  toast.error(emailError(error));
                }
              }}
            >
              Duplicate
            </Button>
            <IconButton
              size="sm"
              variant="plain"
              title={
                d.steps_locked
                  ? "Duplicate to change steps after enrollment"
                  : "Edit sequence"
              }
              disabled={d.steps_locked}
              onClick={() => setEditing(d)}
            >
              <Edit size={18} />
            </IconButton>
            <IconButton
              size="sm"
              variant="plain"
              color="danger"
              onClick={() =>
                window.confirm("Delete this drip campaign?") &&
                remove.mutate(d.id)
              }
            >
              <Delete size={18} />
            </IconButton>
          </Stack>
        )}
        emptyState={
          <Typography level="body-sm">
            {isLoading
              ? "Loading…"
              : dripsError
                ? "Sequences unavailable."
                : "No matching sequences."}
          </Typography>
        }
      />

      {detailEnrollment !== null && (
        <DripEnrollmentDetails
          id={detailEnrollment}
          onClose={() => setDetailEnrollment(null)}
        />
      )}
      <Modal open={!!sequence} onClose={() => setSequence(null)}>
        <ModalDialog
          sx={{
            width: 800,
            maxWidth: "95vw",
            maxHeight: "92vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">{sequence?.name} · Timeline</Typography>
          <Typography level="body-sm">
            Delays are relative to the preceding step. Quota holds, errors, and
            inactive periods extend the baseline below.
          </Typography>
          {sequence?.steps.map((step, index) => (
            <Box
              key={step.id || index}
              sx={{
                pl: 2,
                borderLeft: "3px solid",
                borderColor: "primary.300",
                mb: 2,
              }}
            >
              <Typography level="title-md">
                {index + 1}. {step.template_name}
              </Typography>
              <Typography level="body-sm">
                {sequence.steps
                  .slice(0, index + 1)
                  .reduce(
                    (hours, item) =>
                      hours + item.delay_days * 24 + item.delay_hours,
                    0,
                  )}{" "}
                hours from enrollment · waits {step.delay_days} days,{" "}
                {step.delay_hours} hours after the prior step
              </Typography>
              <Box component="details">
                <Box component="summary" sx={{ cursor: "pointer" }}>
                  Preview email
                </Box>
                <EmailPreview
                  html={
                    templates.find((template) => template.id === step.template)
                      ?.html_content || ""
                  }
                  subject={
                    step.subject ||
                    templates.find((template) => template.id === step.template)
                      ?.subject
                  }
                  sampleControls={false}
                />
              </Box>
            </Box>
          ))}
          <Button onClick={() => setSequence(null)}>Close</Button>
        </ModalDialog>
      </Modal>
      {/* Create / edit */}
      <Modal open={!!editing} onClose={() => setEditing(null)}>
        <ModalDialog
          sx={{
            width: 720,
            maxWidth: "95vw",
            maxHeight: "92vh",
            overflow: "auto",
          }}
        >
          <Typography level="h4">
            {editing?.id ? "Edit Drip Campaign" : "New Drip Campaign"}
          </Typography>
          {editing && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl required>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editing.name || ""}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>Enrollment trigger</FormLabel>
                <Select
                  value={editing.trigger_type || "manual"}
                  onChange={(_, v) =>
                    setEditing({
                      ...editing,
                      trigger_type: v as DripTriggerType,
                    })
                  }
                >
                  {TRIGGERS.map((t) => (
                    <Option key={t.value} value={t.value}>
                      {t.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              {editing.trigger_type === "tag_added" && (
                <FormControl>
                  <FormLabel>Trigger tag</FormLabel>
                  <Select
                    value={editing.trigger_tag ?? null}
                    onChange={(_, v) =>
                      setEditing({ ...editing, trigger_tag: v as number })
                    }
                  >
                    {tags.map((t: any) => (
                      <Option key={t.id} value={t.id}>
                        {t.name}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}
              {editing.trigger_type === "lifecycle_changed" && (
                <FormControl>
                  <FormLabel>Trigger when contact type becomes</FormLabel>
                  <Select
                    value={editing.trigger_lifecycle_stage || ""}
                    onChange={(_, v) =>
                      setEditing({
                        ...editing,
                        trigger_lifecycle_stage: v as string,
                      })
                    }
                  >
                    <Option value="">Any change</Option>
                    {CONTACT_TYPES.map((ct) => (
                      <Option key={ct} value={ct}>
                        {ct}
                      </Option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography level="title-sm">Steps</Typography>
                <Button
                  size="sm"
                  variant="soft"
                  startDecorator={<Add />}
                  onClick={addStep}
                  disabled={templates.length === 0}
                >
                  Add step
                </Button>
              </Box>
              {(editing.steps || []).map((s, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "sm",
                  }}
                >
                  <Typography level="body-sm" sx={{ mb: 1 }}>
                    Step {i + 1} ·{" "}
                    {(editing.steps || [])
                      .slice(0, i + 1)
                      .reduce(
                        (hours, step) =>
                          hours + step.delay_days * 24 + step.delay_hours,
                        0,
                      )}{" "}
                    hours from enrollment before any holds
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems="stretch"
                  >
                    <FormControl sx={{ flex: 2 }}>
                      <FormLabel>Step {i + 1} template</FormLabel>
                      <Select
                        value={s.template}
                        onChange={(_, v) =>
                          setStep(i, { template: v as number })
                        }
                      >
                        {templates.map((t: EmailTemplate) => (
                          <Option key={t.id} value={t.id}>
                            {t.name}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Delay days</FormLabel>
                      <Input
                        type="number"
                        value={s.delay_days}
                        onChange={(e) =>
                          setStep(i, { delay_days: Number(e.target.value) })
                        }
                      />
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <FormLabel>Delay hrs</FormLabel>
                      <Input
                        type="number"
                        value={s.delay_hours}
                        onChange={(e) =>
                          setStep(i, { delay_hours: Number(e.target.value) })
                        }
                      />
                    </FormControl>
                    <IconButton
                      size="sm"
                      color="danger"
                      variant="plain"
                      onClick={() => removeStep(i)}
                    >
                      <Delete size={18} />
                    </IconButton>
                  </Stack>
                </Box>
              ))}
              <Alert size="sm" color="neutral">
                Delays are measured from the previous step (the first step's
                delay is from enrollment time).
              </Alert>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  loading={save.isPending}
                  disabled={!editing.name}
                  onClick={() => save.mutate(editing)}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>

      {/* Enroll */}
      <Modal open={!!enrollFor} onClose={() => setEnrollFor(null)}>
        <ModalDialog sx={{ width: 420, maxWidth: "94vw" }}>
          <Typography level="h4">Enroll contacts</Typography>
          <FormControl sx={{ mt: 2 }}>
            <FormLabel>By contact type</FormLabel>
            <Select
              value={enrollType}
              onChange={(_, v) => setEnrollType(v as string)}
            >
              {CONTACT_TYPES.map((ct) => (
                <Option key={ct} value={ct}>
                  {ct}
                </Option>
              ))}
            </Select>
          </FormControl>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button variant="plain" onClick={() => setEnrollFor(null)}>
              Cancel
            </Button>
            <Button
              disabled={!enrollType}
              loading={enroll.isPending}
              onClick={() => enroll.mutate()}
            >
              Enroll
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Enrollments list */}
      <Modal open={viewEnroll != null} onClose={() => setViewEnroll(null)}>
        <ModalDialog
          sx={{
            width: 640,
            maxWidth: "95vw",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <Typography level="h4" sx={{ mb: 1 }}>
            Enrollments
          </Typography>
          {enrollmentsError && (
            <Alert
              color="danger"
              endDecorator={
                <Button onClick={() => retryEnrollments()}>Retry</Button>
              }
            >
              Enrollments could not be loaded.
            </Alert>
          )}
          {enrollmentsLoading && <Typography>Loading enrollments…</Typography>}
          {enrollments.length === 0 ? (
            <Typography level="body-sm">
              {enrollmentsError
                ? "Enrollments unavailable."
                : enrollmentsLoading
                  ? "Loading…"
                  : "No enrollments."}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {enrollments.map((e) => (
                <Box
                  key={e.id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography level="body-sm">
                    {e.contact_name} ({e.contact_email})
                  </Typography>
                  <Chip size="sm" variant="soft">
                    {e.status} · {e.source}
                  </Chip>
                  <Button
                    size="sm"
                    variant="plain"
                    onClick={() => setDetailEnrollment(e.id)}
                  >
                    Details
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Box>
  );
};
